import { apiFetch } from "../../../shared/apiClient";
import { FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES } from "../../../shared/factset/factsetConstants";
import { isAbortError } from "../../../shared/utils/abortError";

const CREATE_DATA_ROUTE = "/api/ai/preslink/create-data";
const RENDER_ROUTE = "/api/ai/preslink/render";
const DEFAULT_TIMEOUT_MS = 180_000;
const MAX_RESPONSE_TEXT_BYTES = 96 * 1024 * 1024;

/**
 * The server proxy's own error codes. It forwards upstream error bodies
 * verbatim, and those can name the user's local workbook paths, so no error
 * text from a response is ever reused.
 */
const PROXY_ERROR_MESSAGES = new Map<string, string>([
  ["preslink_create_data_api_unconfigured", "Preslink chart creation is not configured on this server."],
  ["preslink_create_data_invalid_request", "Preslink chart creation request is invalid."],
  ["preslink_create_data_request_too_large", "Preslink workbook request exceeds the supported size limit."],
  ["preslink_create_data_auth_unavailable", "Preslink chart creation needs a signed-in Finster session."],
  ["preslink_create_data_response_too_large", "Preslink service response exceeded the supported size limit."],
  ["preslink_create_data_api_timeout", "Preslink service timed out."],
  ["preslink_create_data_api_unavailable", "Preslink service is unavailable right now."],
]);

export interface PreslinkCreateDataSize {
  width: number;
  height: number;
}

export interface PreslinkCreateDataShape {
  targetName: string;
  targetSheet: string;
  targetFile: string;
  size?: PreslinkCreateDataSize;
}

export interface PreslinkExcelFile {
  file: string;
  contents: string;
}

export interface PreslinkCreateDataRequest {
  shapes: PreslinkCreateDataShape[];
  mimeType: "image/png";
  /**
   * The Windows display scale the service corrects a requested size for.
   * Always 100%, so a chart's size never depends on the user's display.
   */
  monitorScale: 1;
  excelFiles: PreslinkExcelFile[];
}

export interface PreslinkCreatedShape {
  tags: Record<string, string>;
  image: string;
}

export interface PreslinkCreateDataResponse {
  shapes: PreslinkCreatedShape[];
  excelFiles: PreslinkExcelFile[];
}

export interface PreslinkRenderRequest {
  tags: Array<Record<string, string>>;
  mimeType: "image/png";
  excelFiles: PreslinkExcelFile[];
}

export interface PreslinkRenderResponse {
  shapes: Array<{ image: string }>;
}

export interface PreslinkCreateDataRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class PreslinkCreateDataError extends Error {
  readonly code: string;
  readonly status?: number;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = "PreslinkCreateDataError";
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, PreslinkCreateDataError.prototype);
  }
}

function compactBase64(value: unknown): string {
  return typeof value === "string" ? value.replace(/\s+/g, "") : "";
}

function decodedLength(base64: string): number {
  if (!base64) return 0;
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

function isPngBase64(value: string): boolean {
  try {
    const bytes = atob(value.slice(0, 16));
    return (
      bytes.length >= 8 &&
      bytes.charCodeAt(0) === 0x89 &&
      bytes.slice(1, 4) === "PNG" &&
      bytes.charCodeAt(4) === 0x0d &&
      bytes.charCodeAt(5) === 0x0a &&
      bytes.charCodeAt(6) === 0x1a &&
      bytes.charCodeAt(7) === 0x0a
    );
  } catch {
    return false;
  }
}

function assertWorkbookFiles(value: unknown): PreslinkExcelFile[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new PreslinkCreateDataError(
      "preslink_create_data_invalid_response",
      "Preslink service returned no Excel workbook.",
    );
  }
  return value.map((entry) => {
    if (!entry || typeof entry !== "object") {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_response",
        "Preslink service returned an invalid Excel file entry.",
      );
    }
    const object = entry as Record<string, unknown>;
    const file = typeof object.file === "string" ? object.file : "";
    const contents = compactBase64(object.contents);
    if (!file || !contents || decodedLength(contents) > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES) {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_response",
        "Preslink service returned an invalid or oversized Excel workbook.",
      );
    }
    return { file, contents };
  });
}

function assertCreatedShapes(
  value: unknown,
  expectedCount: number,
): PreslinkCreatedShape[] {
  if (!Array.isArray(value) || value.length !== expectedCount) {
    throw new PreslinkCreateDataError(
      "preslink_create_data_shape_count_mismatch",
      "Preslink service returned a different number of shapes than requested.",
    );
  }
  return value.map((entry) => {
    if (!entry || typeof entry !== "object") {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_response",
        "Preslink service returned an invalid shape.",
      );
    }
    const object = entry as Record<string, unknown>;
    const image = compactBase64(object.image);
    const tags =
      object.tags && typeof object.tags === "object" && !Array.isArray(object.tags)
        ? Object.fromEntries(
            Object.entries(object.tags as Record<string, unknown>).filter(
              (item): item is [string, string] => typeof item[1] === "string",
            ),
          )
        : {};
    if (!isPngBase64(image) || Object.keys(tags).length === 0) {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_response",
        "Preslink service returned a shape without valid PNG bytes and tags.",
      );
    }
    return { image, tags };
  });
}

function assertRenderShapes(value: unknown, expectedCount: number): Array<{ image: string }> {
  if (!Array.isArray(value) || value.length !== expectedCount) {
    throw new PreslinkCreateDataError(
      "preslink_render_shape_count_mismatch",
      "Preslink render verification returned a different number of shapes than requested.",
    );
  }
  return value.map((entry) => {
    const image =
      entry && typeof entry === "object"
        ? compactBase64((entry as Record<string, unknown>).image)
        : "";
    if (!isPngBase64(image)) {
      throw new PreslinkCreateDataError(
        "preslink_render_invalid_response",
        "Preslink render verification returned an invalid PNG image.",
      );
    }
    return { image };
  });
}

function validateRequestFiles(files: PreslinkExcelFile[]): void {
  if (files.length === 0) {
    throw new PreslinkCreateDataError(
      "preslink_create_data_invalid_request",
      "A source Excel workbook is required.",
    );
  }
  for (const file of files) {
    const contents = compactBase64(file.contents);
    if (
      !file.file ||
      !contents ||
      decodedLength(contents) > FACTSET_CHART_CREATOR_RESPONSE_MAX_BYTES
    ) {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_request",
        "The source Excel workbook is invalid or exceeds the size limit.",
      );
    }
  }
}

async function postJson(
  route: string,
  body: unknown,
  options: PreslinkCreateDataRequestOptions,
): Promise<unknown> {
  const controller = new AbortController();
  const onAbort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) controller.abort(options.signal.reason);
  else options.signal?.addEventListener("abort", onAbort, { once: true });
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const response = await apiFetch(route, {
      method: "POST",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const declaredLength = Number(response.headers.get("content-length"));
    if (Number.isFinite(declaredLength) && declaredLength > MAX_RESPONSE_TEXT_BYTES) {
      throw new PreslinkCreateDataError(
        "preslink_create_data_response_too_large",
        "Preslink service response exceeded the size limit.",
        response.status,
      );
    }
    const text = await response.text();
    if (new TextEncoder().encode(text).byteLength > MAX_RESPONSE_TEXT_BYTES) {
      throw new PreslinkCreateDataError(
        "preslink_create_data_response_too_large",
        "Preslink service response exceeded the size limit.",
        response.status,
      );
    }
    let payload: unknown;
    try {
      payload = text ? JSON.parse(text) : {};
    } catch {
      throw new PreslinkCreateDataError(
        "preslink_create_data_invalid_response",
        "Preslink service returned invalid JSON.",
        response.status,
      );
    }
    if (!response.ok) {
      const code =
        payload && typeof payload === "object"
          ? (payload as Record<string, unknown>).error
          : undefined;
      const message = typeof code === "string" ? PROXY_ERROR_MESSAGES.get(code) : undefined;
      throw new PreslinkCreateDataError(
        message ? String(code) : "preslink_create_data_failed",
        message ?? `Preslink service request failed (${response.status}).`,
        response.status,
      );
    }
    return payload;
  } catch (error) {
    if (isAbortError(error)) {
      if (timedOut) {
        throw new PreslinkCreateDataError(
          "preslink_create_data_timeout",
          "Preslink service timed out.",
        );
      }
      throw error;
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", onAbort);
  }
}

export async function createPreslinkData(
  request: PreslinkCreateDataRequest,
  options: PreslinkCreateDataRequestOptions = {},
): Promise<PreslinkCreateDataResponse> {
  if (!Array.isArray(request.shapes) || request.shapes.length === 0) {
    throw new PreslinkCreateDataError(
      "preslink_create_data_invalid_request",
      "At least one Preslink target is required.",
    );
  }
  validateRequestFiles(request.excelFiles);
  const payload = await postJson(CREATE_DATA_ROUTE, request, options);
  const object =
    payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
  return {
    shapes: assertCreatedShapes(object.shapes, request.shapes.length),
    excelFiles: assertWorkbookFiles(object.excelFiles),
  };
}

export async function renderPreslinkTags(
  request: PreslinkRenderRequest,
  options: PreslinkCreateDataRequestOptions = {},
): Promise<PreslinkRenderResponse> {
  if (!Array.isArray(request.tags) || request.tags.length === 0) {
    throw new PreslinkCreateDataError(
      "preslink_render_invalid_request",
      "At least one Preslink tag set is required.",
    );
  }
  validateRequestFiles(request.excelFiles);
  const payload = await postJson(RENDER_ROUTE, request, options);
  const object =
    payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
  return { shapes: assertRenderShapes(object.shapes, request.tags.length) };
}
